import { useEffect, useState, useContext } from "react";
import CIIGraph from "../../components/ReportDetails/child-reports/DCSReport/DCSReportedData/CIIGraph/CIIGraph";
import { Controller, useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { MDBCol, MDBRow } from "mdbreact";

import "./CIICalculator.scss";

import { AbsDropDown, Input, Label } from "abs-ui-components";

import {
	calculateCIIData,
	searchVesselWithIMoOptions,
	getFuelRegistry
} from "../../services/CIIData-service";
import { getMasterData } from "../../store/myFleet/myFleet.actions";
import AutocompleteDropdown from "../../common/autocompleteDropdown/AutocompleteDropdown";
import { ClaimsContext } from "../../services/ClaimsService";
import { FeatureType } from "../../constants/FeatureToggles";

import {
	CAPACITY_UNIT,
	CII_INSTRUCTIONS,
	FUEL_CONSUMPTIONS_HEADER,
	FUEL_CONSUMPTION_FORM_INPUTS,
	VESSEL_DETAILS_FORM_HEADER,
	NM_UNIT_TEXT,
	MT_UNIT_TEXT,
	INSTRUCTION_TEXT,
	PRELIMINARY_CII_RATING_HEADER,
	CALCULATE_BTN_TEXT,
	CII_TABLE,
} from "./CIICalculatorConstants";

const CIICalculator = () => {
	const [vesselName, setVesselName] = useState("");
	const [CIIGraphData, setCIIGraphData] = useState({});
	const [IMONumber, setIMONumber] = useState();
	const [showCIIGraph, setShowCIIGraph] = useState(false);
	const {
		control,
		formState: { errors },
		handleSubmit,
		getValues,
		setValue,
		watch,
	} = useForm();
	const masterData = useSelector((state) => state.myFleet.masterData);
	const ciiFormData = getValues("ciiFormData");
	const dispatch = useDispatch();

	const [vesselFuels, setvesselFuels] = useState();

	const mapAndSetVesselFuelsAsync = async () => {

		const fuels = await getFuelRegistry(ciiFormData?.vesselId?.value);

		// Map fuels (exclude blends)
		const mappedFuels =
			fuels
				?.filter(f => f.categoryType !== "Blend")
				.map(f => ({
					label: f.name || f.code,
					controlName: f.code,
					required: false,
				})) || [];

		// Distinct by controlName
		const distinctFuels = [
			...new Map(mappedFuels.map(f => [f.controlName, f])).values(),
			{
				label: "Dist Travelled",
				controlName: "distTravelled",
				required: true,
			}
		];

		// Use functional update to avoid stale state closures
		setvesselFuels(() => distinctFuels);
	};

	let selectedVesselType = null;

	useEffect(() => {
		watch(ciiFormData?.vesselId);
		if (ciiFormData?.vesselId) {
			setVesselName(ciiFormData.vesselId?.label);
			setValue("ciiFormData.imoNumber", ciiFormData?.vesselId?.imoNumber);
			setValue("ciiFormData.capacity", ciiFormData.vesselId?.capacity);
			if (imoVesselTypes) {
				let selectedIMOType = imoVesselTypes.find(
					(obj) =>
						obj.ImoVesselTypeId ===
						ciiFormData.vesselId?.imoVesselTypeId
				);
				if (selectedIMOType) {
					setValue("ciiFormData.vesselType", selectedIMOType);
					selectedVesselType = selectedIMOType;
				}
			}
			mapAndSetVesselFuelsAsync();
		} else {
			setValue("ciiFormData.imoNumber", "");
			setValue("ciiFormData.capacity", "");
		}
	}, [ciiFormData?.vesselId, watch]);

	useEffect(() => {
		if (Object.keys(masterData).length === 0) {
			dispatch(getMasterData());
		}
	}, []);

	// imo vessel type drop down options
	const imoVesselTypes =
		masterData[1] && masterData[1].GetImoVesselType
			? masterData[1].GetImoVesselType.map((e) => ({
				...e,
				value: e.Value,
				label: e.Value,
			}))
			: [];

	// Auto complete for vessel
	const loadSearchVesselOptions = async (inputValue) => {
		if (inputValue) {
			let searchInputValue = { searchKey: inputValue };
			let response;
			try {
				response = await searchVesselWithIMoOptions(searchInputValue);
			} catch (error) {
				console.error(error, "Failed to Calculate the CII"); // Fallback in case of error
			}
			let loadedVesselWithImo = [];
			if (response) {
				loadedVesselWithImo = response.data;
			}
			const formattedVesselWithIMOOptions = loadedVesselWithImo?.map(
				(item) => {
					return {
						...item,
						label: `${item?.vesselName} `,
						value: `${item?.vesselMstId}`,
					};
				}
			);
			return formattedVesselWithIMOOptions;
		}
	};

	// submiting cii form data
	const postCiiDataForm = async () => {
		setShowCIIGraph(false);
		let payLoad = {
			vesselId: +ciiFormData?.vesselId?.value,
			imoNumber: +ciiFormData?.imoNumber,
			vesselName: vesselName,
			vesselTypeId: +ciiFormData?.vesselType
				? +ciiFormData?.vesselType
				: imoVesselTypes[0].ImoVesselTypeId,
			capacity: +ciiFormData?.capacity,
			hfoCons: +ciiFormData?.HFO ? +ciiFormData?.HFO : 0,
			lfoCons: +ciiFormData?.LFO ? +ciiFormData?.LFO : 0,
			mdoCons: +ciiFormData?.MDO ? +ciiFormData?.MDO : 0,
			mgoCons: +ciiFormData?.MGO ? +ciiFormData?.MGO : 0,
			lpgpCons: +ciiFormData?.LPGPropane ? +ciiFormData?.LPGPropane : 0,
			lpgbCons: +ciiFormData?.LPGButane ? +ciiFormData?.LPGButane : 0,
			lngCons: +ciiFormData?.LNG ? +ciiFormData?.LNG : 0,
			methanolCons: +ciiFormData?.methanol ? +ciiFormData?.methanol : 0,
			ethanolCons: +ciiFormData?.ethanol ? +ciiFormData?.ethanol : 0,
			otherFuel1: +ciiFormData?.otherFuel1 ? +ciiFormData?.otherFuel1 : 0,
			otherFuel2: +ciiFormData?.otherFuel2 ? +ciiFormData?.otherFuel2 : 0,
			otherFuel3: +ciiFormData?.otherFuel3 ? +ciiFormData?.otherFuel3 : 0,
			distanceTravelled: +ciiFormData?.distTravelled,
		};

		const fuelCons =
			vesselFuels
				?.filter(fuel => fuel.controlName !== "distTravelled")
				.reduce((acc, fuel) => {
					const key = fuel.controlName;
					const value = ciiFormData?.[key];

					if (value != null) {
						acc[key] = value;
					}

					return acc;
				}, {}) || {};

		payLoad.fuelCons = fuelCons;

		let response = await calculateCIIData(payLoad);
		if (response) {
			setShowCIIGraph(true);
			setCIIGraphData(response?.data);
		}
	};

	return (
		<div className="cii-dashboard col-md-12">
			<div className="d-flex justify-content-between cii-form-graph-container">
				<div className="col-md- 3 cii-dashboard-form">
					<form onSubmit={handleSubmit(postCiiDataForm)}>
						<MDBCol md="12" className="cii-header">
							<span>{VESSEL_DETAILS_FORM_HEADER}</span>
						</MDBCol>
						<MDBRow className="form-row">
							<MDBCol md="12" className="mt-3">
								<Label required name="Search Vessel"></Label>
								<Controller
									name="ciiFormData.vesselId"
									rules={{
										required: "Vessel is required",
									}}
									control={control}
									render={({ field }) => {
										return (
											<AutocompleteDropdown
												className="inputHeight"
												id="search-vessel-id"
												{...field}
												isClearable
												loadOptions={
													loadSearchVesselOptions
												}
												placeholder="Search Vessel"
												getOptionLabel={(option) =>
													`${option?.label} (${option?.imoNumber})`
												}
												autoFocus={false}
												onChange={(option) => {
													field.onChange(option);
													if (option) {
														setValue(
															"ciiFormData.capacity",
															option.capacity,
															{
																shouldValidate: true,
															}
														);
													} else {
														setValue(
															"ciiFormData.capacity",
															"",
															{
																shouldValidate: true,
															}
														);
													}
												}}
											/>
										);
									}}
								/>
								{errors.ciiFormData &&
									errors.ciiFormData.vesselId && (
										<div className="error">
											{
												errors.ciiFormData.vesselId
													.message
											}
										</div>
									)}
							</MDBCol>
							<MDBCol md="12" className="mt-3">
								<Label name="IMO Number" className="mb-1" />
								<Controller
									name="ciiFormData.imoNumber"
									control={control}
									render={({ field }) => (
										<Input
											className="input-height"
											{...field}
											autoFocus={false}
											disabled
										/>
									)}
								/>
							</MDBCol>
							<MDBCol md="12" className="mt-3 choose-year">
								<Label name="IMO Vessel Type"></Label>
								<Controller
									name="ciiFormData.vesselType"
									control={control}
									defaultValue={
										selectedVesselType || imoVesselTypes[0]
									}
									render={({ field }) => (
										<AbsDropDown
											name="imo_vessel_type"
											selected={field.value?.label}
											handleChange={(e) => {
												field.onChange(e);
												setValue(
													"ciiFormData.vesselType",
													e
												);
											}}
											options={imoVesselTypes}
										/>
									)}
								/>
							</MDBCol>
							<MDBCol md="12" className="mt-3">
								<Label
									name="Capacity"
									required
									className="mb-1"
								/>
								<Controller
									name="ciiFormData.capacity"
									control={control}
									rules={{
										required: "Capacity is required",
										min: {
											value: 1,
											message:
												"Capacity must be greater than 0",
										},
										pattern: {
											value: /^[0-9]*$/,
											message:
												"Capacity must be a valid number",
										},
									}}
									render={({ field }) => (
										<div className="d-flex">
											<MDBCol
												md="10"
												className="removed-initial-padding"
											>
												<>
													<Input
														className="input-height"
														{...field}
														autoFocus={false}
														onInput={(e) => {
															e.target.value =
																e.target.value.replace(
																	/[^0-9]/g,
																	""
																);
														}}
													/>
												</>
											</MDBCol>

											<MDBCol
												md="2"
												className="removed-initial-padding"
											>
												<div className="d-flex justify-content-center align-items-center cii-unit">
													<span>{CAPACITY_UNIT}</span>
												</div>
											</MDBCol>
										</div>
									)}
								/>
								{errors.ciiFormData &&
									errors.ciiFormData.capacity && (
										<div className="error">
											{
												errors.ciiFormData.capacity
													.message
											}
										</div>
									)}
							</MDBCol>

							<MDBCol md="12" className="mt-3 form-card-header">
								<span>{FUEL_CONSUMPTIONS_HEADER}</span>
							</MDBCol>

							{/* adding input fields dynamically  */}

							{(vesselFuels?.length > 0 ? vesselFuels : (FUEL_CONSUMPTION_FORM_INPUTS || [])).map(
								(fuelObject) => {
									return (
										<MDBCol md="12" className="mt-3">
											<Label
												name={fuelObject.label}
												className="mb-1"
												required={
													fuelObject.controlName ===
														"distTravelled"
														? fuelObject.required
														: false
												}
											/>
											<Controller
												name={`ciiFormData.${fuelObject.controlName}`}
												control={control}
												rules={{
													required:
														fuelObject.controlName ===
															"distTravelled"
															? "Distance Travelled is required"
															: false,
													min: {
														value: 1,
														message:
															"Distance must be greater than 0",
													},
													pattern: {
														value: /^[0-9]*$/,
														message:
															"Distance must be a valid number",
													},
												}}
												render={({ field }) => (
													<div className="d-flex">
														<MDBCol
															md="10"
															className="removed-initial-padding"
														>
															<Input
																className="input-height"
																{...field}
																autoFocus={
																	false
																}
																onInput={(
																	e
																) => {
																	e.target.value =
																		e.target.value.replace(
																			/[^0-9]/g,
																			""
																		);
																}}
															/>
														</MDBCol>
														<MDBCol
															md="2"
															className="removed-initial-padding"
														>
															<div className="d-flex justify-content-center align-items-center cii-unit">
																<span>
																	{fuelObject.controlName ===
																		"distTravelled"
																		? NM_UNIT_TEXT
																		: MT_UNIT_TEXT}
																</span>
															</div>
														</MDBCol>
													</div>
												)}
											/>
											{fuelObject.controlName ===
												"distTravelled" &&
												errors.ciiFormData &&
												errors.ciiFormData
													.distTravelled && (
													<div className="error">
														{
															errors
																.ciiFormData
																.distTravelled
																.message
														}
													</div>
												)}
										</MDBCol>
									);
								}
							)}
							<MDBCol
								md="12"
								className="d-flex justify-content-center align-items-center mt-3"
							>
								<button
									type="submit"
									className="mr-3 save-button"
								>
									{CALCULATE_BTN_TEXT}
								</button>
							</MDBCol>
						</MDBRow>
					</form>
				</div>
				<div className="col-md-9 cii-graph">
					<div className="col-md-12 cii-header">
						{PRELIMINARY_CII_RATING_HEADER}
					</div>
					{/* future scope */}
					{/* {showCIIGraph ? (
						<>
							<div className="text-center pt-3 pb-2 cii-table-container pl-5 pr-5 ">
								<Table columns={CII_TABLE} data={[]} />
								<table className="table table-bordered ">
									<tbody>
										<tr>
											<td className="font-weight-bold text-left width-adjust">
												Year
											</td>
											<td className="font-weight-bold">
												2024
											</td>
											<td className="font-weight-bold">
												2025
											</td>
											<td className="font-weight-bold">
												2026
											</td>
										</tr>
										<tr>
											<td className="font-weight-bold text-left">
												Required CII (gCO2/t.NM)
											</td>
											<td className="k-text-align-right ">
												18.76
											</td>
											<td className="k-text-align-right">
												18.36
											</td>
											<td className="k-text-align-right">
												17.95
											</td>
										</tr>
										<tr>
											<td className="font-weight-bold text-left">
												Attained CII (gCO2/t.NM)
											</td>
											<td className="k-text-align-right">
												0.00
											</td>
											<td className="k-text-align-right">
												0.00
											</td>
											<td className="k-text-align-right">
												0.00
											</td>
										</tr>
										<tr>
											<td className="font-weight-bold text-left">
												Attained Rating
											</td>
											<td className="k-text-align-center">
												{
													CIIGraphData.attainedCIIGradeWithoutExclusion
												}
											</td>
											<td className="k-text-align-center">
												{
													CIIGraphData.attainedCIIGradeWithoutExclusion
												}
											</td>
											<td className="k-text-align-center">
												{
													CIIGraphData.attainedCIIGradeWithoutExclusion
												}
											</td>
										</tr>
									</tbody>
								</table>
							</div>
						</>
					) : ( */}
					{/* )} */}
					<>
						<div>
							<h3
								style={{ textAlign: "center" }}
								className="pt-2 pb-2"
							>
								{INSTRUCTION_TEXT}
							</h3>
							<div className="d-flex justify-content-center">
								<ul>
									{CII_INSTRUCTIONS.map(
										(instruction, index) => (
											<li key={index}>{instruction}</li>
										)
									)}
								</ul>
							</div>
						</div>
					</>

					<CIIGraph
						isCalculatorPage={true}
						showGraph={showCIIGraph}
						CIICalculatedData={CIIGraphData}
					/>
				</div>
			</div>
		</div>
	);
};

export default CIICalculator;
